import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, Check, Wifi, Zap, Lock, History, LayoutDashboard, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { PLANS, formatPrice } from "@/lib/pricing";
import { CompareTable } from "@/components/CompareTable";
import logo from "@/assets/logo.png";

const PLAN_POINTS = {
  monthly: ["Semua fitur premium", "Grup diskusi dengan expert finance CeKKeU", "Cocok untuk coba dulu"],
  sixmonth: ["Semua fitur premium", "Grup diskusi dengan expert finance CeKKeU", "Hemat 15% vs bulanan"],
  annual: ["Semua fitur premium", "Grup diskusi dengan expert finance CeKKeU", "Hemat 32% — termurah/bln"],
};

const points = [
  { icon: Check, text: "Gratis untuk memulai" },
  { icon: Wifi, text: "Tidak perlu koneksi rekening bank" },
  { icon: Zap, text: "Hasil langsung" },
  { icon: Lock, text: "Angka keuangan Anda tidak dikirim ke server kami" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { report } = useApp();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    track("landing_page_view");
  }, []);

  const returningLocal = report && !isAuthenticated;

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-5xl bg-white">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 pt-6">
        <div className="flex items-center gap-2">
          <img
            src={logo}
            alt="CeKKeU - Cek Keuangan untuk Kesejahteraanmu"
            data-testid="landing-logo"
            className="h-24 w-auto sm:h-28"
          />
        </div>
        {(report || isAuthenticated) && (
          <button
            data-testid="landing-open-dashboard"
            onClick={() => navigate("/app/beranda")}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100"
          >
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </button>
        )}
        {!report && isAdmin && (
          <button
            data-testid="landing-admin-link"
            onClick={() => navigate("/admin")}
            className="text-xs font-semibold text-emerald-600"
          >
            Admin
          </button>
        )}
        {!report && !isAuthenticated && (
          <button
            data-testid="landing-login-link"
            onClick={() => navigate("/login")}
            className="text-xs font-semibold text-zinc-500"
          >
            Masuk
          </button>
        )}
      </header>

      {/* Hero */}
      <section className="px-6 pb-8 pt-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Stress Test Keuangan Keluarga
          </div>

          <h1 className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-zinc-900 sm:text-4xl">
            Kalau penghasilan Anda terganggu bulan depan, Anda bisa{" "}
            <span className="text-emerald-600">bertahan berapa lama?</span>
          </h1>

          <p className="mt-5 text-base leading-relaxed text-zinc-500">
            Cek ketahanan keuangan keluarga dalam 2 menit di tengah ekonomi tak
            menentu.{" "}
            <span className="font-semibold text-[#800000]">
              Input Pemasukan dan Pengeluaran Harian untuk jurnal dan Lakukan
              Analisa Anti Boncos
            </span>
          </p>

          {returningLocal ? (
            <div className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-5" data-testid="landing-returning">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-emerald-600" />
                <p className="font-heading text-sm font-bold text-emerald-800">Selamat datang kembali</p>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-emerald-700">
                Kami menemukan hasil pemeriksaan terakhir Anda di perangkat ini.
              </p>
              <button
                data-testid="landing-view-last"
                onClick={() => navigate("/result")}
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-semibold text-white"
              >
                Lihat hasil terakhir <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="landing-recheck"
                onClick={() => navigate("/assessment")}
                className="mt-2 flex h-11 w-full items-center justify-center rounded-full border border-emerald-300 text-sm font-semibold text-emerald-700"
              >
                Cek ulang kondisi saya
              </button>
              <button
                data-testid="landing-journal-cta"
                onClick={() => navigate("/paywall?next=/app/catat")}
                className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#800000] text-sm font-semibold text-white hover:bg-[#6b0000]"
              >
                Catat Pemasukan dan Pengeluaran Harian
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="landing-login-daftar"
                onClick={() => navigate("/login")}
                className="mt-3 w-full text-center text-xs font-semibold text-emerald-700 underline"
              >
                Masuk / Daftar untuk menggunakan CeKKeU
              </button>
            </div>
          ) : (
            <>
              <Button
                data-testid="landing-cta"
                onClick={() => navigate("/assessment")}
                className="mt-8 h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
              >
                Cek Ketahanan Keuangan Gratis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="landing-journal-cta"
                onClick={() => navigate("/paywall?next=/app/catat")}
                className="mt-3 h-14 w-full rounded-full bg-[#800000] text-base font-semibold text-white hover:bg-[#6b0000]"
              >
                Catat Pemasukan dan Pengeluaran Harian
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <button
                data-testid="landing-secondary-login"
                onClick={() => navigate("/login")}
                className="mt-3 w-full text-center text-sm font-semibold text-zinc-600"
              >
                Sudah punya akun? <span className="text-emerald-600">Masuk</span>
              </button>
            </>
          )}

          {/* Primary privacy reassurance — visible before starting */}
          <div
            data-testid="landing-privacy"
            className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50 p-4"
          >
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-emerald-600" />
              <p className="font-heading text-sm font-bold text-emerald-800">
                Data keuangan Anda tetap pribadi 🔒
              </p>
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-emerald-700">
              Angka seperti penghasilan, tabungan, cicilan, utang, dan pengeluaran hanya
              diproses di HP atau komputer Anda dan tidak dikirim ke server kami. Kami tidak
              dapat melihat angka-angka keuangan yang Anda masukkan.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Supporting points */}
      <section className="px-6">
        <div className="grid grid-cols-1 gap-3">
          {points.map(({ icon: Icon, text }, i) => (
            <motion.div
              key={text}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.08 }}
              className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-zinc-50/60 px-4 py-3"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm">
                <Icon className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-zinc-700">{text}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gratis vs Premium comparison */}
      <section className="px-6 pt-10" data-testid="landing-compare">
        <h2 className="font-heading text-lg font-extrabold tracking-tight text-zinc-900">
          Gratis vs Premium
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Lihat persis apa yang Anda dapatkan di tiap paket.
        </p>
        <div className="mt-4">
          <CompareTable />
        </div>
      </section>

      {/* Pricing comparison */}
      <section className="px-6 pb-4 pt-12" data-testid="landing-pricing">
        <h2 className="font-heading text-lg font-extrabold tracking-tight text-zinc-900">
          Pilih paket yang pas
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Mulai gratis. Upgrade kapan saja untuk fitur lengkap.
        </p>

        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="landing-community-highlight">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-emerald-600" />
            <p className="font-heading text-sm font-bold text-emerald-800">
              Komunitas Premium CeKKeU 👥
            </p>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-emerald-700">
            Setiap member Premium bisa bergabung ke grup diskusi dan bertanya langsung
            dengan expert finance CeKKeU — dapatkan masukan, tips, dan jawaban seputar
            keuangan keluarga Anda.
          </p>
        </div>

        <div className="mt-5 space-y-3">
          {/* Free */}
          <div className="rounded-3xl border border-zinc-200 bg-white p-5" data-testid="landing-plan-free">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="font-heading text-sm font-bold text-zinc-900">Free</p>
                <p className="text-[11px] text-zinc-400">Financial check dasar</p>
              </div>
              <span className="font-mono text-2xl font-black text-zinc-900">Rp0</span>
            </div>
            <ul className="mt-3 space-y-1.5">
              {["Cek ketahanan keuangan", "Skor & runway ringkas", "Tanpa perlu akun"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-xs text-zinc-600">
                  <Check className="h-3.5 w-3.5 text-emerald-600" /> {t}
                </li>
              ))}
            </ul>
            <button
              data-testid="landing-plan-free-cta"
              onClick={() => navigate("/assessment")}
              className="mt-4 flex h-11 w-full items-center justify-center rounded-full border border-zinc-300 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
            >
              Mulai Gratis
            </button>
          </div>

          {/* Premium plans */}
          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`landing-plan-${p.id}`}
              className={`rounded-3xl border p-5 ${
                p.highlight ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-200" : "border-zinc-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-heading text-sm font-bold text-zinc-900">Premium {p.label}</p>
                    {p.tagline && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.highlight ? "bg-emerald-600 text-white" : "bg-zinc-100 text-emerald-700"}`}>
                        {p.tagline}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500">
                    {formatPrice(p.perMonth)}/bulan{p.months > 1 ? ` · ditagih ${formatPrice(p.price)}` : ""}
                  </p>
                </div>
                <span className="font-mono text-2xl font-black text-zinc-900">{formatPrice(p.price)}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {(PLAN_POINTS[p.id] || []).map((t) => (
                  <li key={t} className="flex items-center gap-2 text-xs text-zinc-600">
                    <Check className="h-3.5 w-3.5 text-emerald-600" /> {t}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`landing-plan-${p.id}-cta`}
                onClick={() => navigate(`/paywall?plan=${p.id}&next=/app/catat`)}
                className={`mt-4 flex h-11 w-full items-center justify-center rounded-full text-sm font-semibold ${
                  p.highlight ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-zinc-900 text-white hover:bg-zinc-800"
                }`}
              >
                Pilih Premium {p.label}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Trust statement */}
      <section className="px-6 pb-12 pt-10">
        <div className="rounded-3xl bg-zinc-900 p-6">
          <ShieldCheck className="h-6 w-6 text-emerald-400" />
          <p className="mt-3 text-sm leading-relaxed text-zinc-300">
            CeKKeU membantu Anda memahami ketahanan cashflow
            keluarga. Bukan pengganti nasihat keuangan profesional.
          </p>
        </div>
        <p className="mt-6 text-center text-xs text-zinc-400">
          Family Financial Stress Test &amp; Resilience Platform
        </p>
      </section>
    </div>
  );
}
